import React, { useEffect, useState, useCallback } from 'react';
import { format } from 'date-fns';
import {
  Box,
  Typography,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { 
  getWorkbookId, 
  getCachedFilterOptions, 
  getCachedData,
  invalidateCacheOnComplete, 
  markItemComplete 
} from '../../services/plmsService';
import { OnboardingItem, PLMSFilters } from '../../types/plms';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ExternalLink, CheckCircle, Circle } from 'lucide-react';

export const AllItems: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<OnboardingItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<PLMSFilters>({
    completionStatus: 'all'
  });
  const [sections, setSections] = useState<string[]>([]);
  const [days, setDays] = useState<number[]>([]);
  const [completingItems, setCompletingItems] = useState<{ [key: number]: boolean }>({});
  const [isLoading, setIsLoading] = useState(false);

  const loadFilterOptions = useCallback(async () => {
    if (!user?.email) return;

    try {
      const workbookId = await getWorkbookId(user.email);
      if (!workbookId) {
        setError('No workbook assigned to your account');
        return;
      }

      const allItems = await getCachedFilterOptions(user.email, workbookId);
      const uniqueSections = Array.from(new Set(allItems.map((item: OnboardingItem) => item.section)))
        .filter(Boolean)
        .sort() as string[];

      const uniqueDays = Array.from(new Set(allItems.map((item: OnboardingItem) => item.day)))
        .filter((day): day is number => day !== undefined && day !== null)
        .sort((a: number, b: number) => {
          if (a === 0) return -1;
          if (b === 0) return 1;
          return a - b;
        });

      setSections(uniqueSections);
      setDays(uniqueDays);
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  }, [user?.email, setError, setSections, setDays]);

  const loadItems = useCallback(async (newPage: number) => {
    if (isLoading || !user?.email) return;
    
    setIsLoading(true);
    try {
      const workbookId = await getWorkbookId(user.email);
      if (!workbookId) {
        setError('No workbook assigned to your account');
        return;
      }

      const { items: newItems, hasMore: moreItems } = await getCachedData(user.email, workbookId, {
        ...filters,
        includeMasterLists: false
      }, newPage);
      
      if (newPage === 0) {
        setItems(newItems);
      } else {
        setItems(prev => [...prev, ...newItems]);
      }
      setHasMore(moreItems);
      setPage(newPage);
      setError(null);
    } catch (error) {
      console.error('Error loading items:', error);
      setError('Failed to load items');
    } finally {
      setIsLoading(false);
    }
  }, [user?.email, filters, isLoading, setIsLoading, setItems, setHasMore, setPage, setError]);

  const handleFilterChange = (filterName: string, value: any) => {
    setIsLoading(true);
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleMarkComplete = async (rowNumber: number) => {
    if (!user?.email) return;
    
    try {
      const workbookId = await getWorkbookId(user.email);
      if (!workbookId) return;

      setCompletingItems(prev => ({ ...prev, [rowNumber]: true }));
      const date = format(new Date(), 'MM/dd/yyyy');
      await markItemComplete(workbookId, rowNumber, date);
      await invalidateCacheOnComplete(user.email);
      await loadItems(0); // Reload first page
    } catch (error) {
      console.error('Error marking item complete:', error);
    } finally {
      setCompletingItems(prev => ({ ...prev, [rowNumber]: false }));
    }
  };

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    setItems([]);
    setPage(0);
    setHasMore(true);
    loadItems(0);
  }, [loadItems, setItems, setPage, setHasMore]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>All Learning Items</Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Section</InputLabel>
          <Select
            value={filters.section || ''}
            onChange={(e) => handleFilterChange('section', e.target.value)}
            label="Section"
          >
            <MenuItem value="">All Sections</MenuItem>
            {sections.map((section) => (
              <MenuItem key={section} value={section}>{section}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 100, mr: 2 }} size="small">
          <InputLabel>Day</InputLabel>
          <Select
            value={filters.day ?? ''}
            onChange={(e) => handleFilterChange('day', e.target.value === '' ? undefined : Number(e.target.value))}
            label="Day"
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {days.map((day) => (
              <MenuItem key={day} value={day}>
                Day {day}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.completionStatus || 'all'}
            onChange={(e) => handleFilterChange('completionStatus', e.target.value as any)}
            label="Status"
          >
            <MenuItem value="all">All Items</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="notCompleted">Not Completed</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box 
        sx={{ 
          width: '100%', 
          height: 'calc(100vh - 250px)', 
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }} 
        id="scrollableDiv"
      >
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 100px 2fr 2fr 60px 60px 150px',
          gap: 2,
          p: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}>
          <Typography variant="subtitle2">Section</Typography>
          <Typography variant="subtitle2">Day</Typography>
          <Typography variant="subtitle2">Item</Typography>
          <Typography variant="subtitle2">Note</Typography>
          <Typography variant="subtitle2">Link</Typography>
          <Typography variant="subtitle2"></Typography>
          <Typography variant="subtitle2">Date Done</Typography>
        </Box>

        <InfiniteScroll
          dataLength={items.length}
          next={() => !isLoading && loadItems(page + 1)}
          hasMore={hasMore}
          loader={<Box sx={{ textAlign: 'center', p: 2 }}><CircularProgress /></Box>}
          scrollableTarget="scrollableDiv"
          endMessage={
            <Box sx={{ textAlign: 'center', p: 2, color: 'text.secondary' }}>
              <Typography variant="body2">No more items to load</Typography>
            </Box>
          }
        >
          {items.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 100px 2fr 2fr 60px 60px 150px',
                gap: 2,
                p: 2,
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                bgcolor: item.dateDone ? '#f0f7f0' : 'transparent',
                '&:hover': {
                  bgcolor: item.dateDone ? '#e5f2e5' : 'action.hover',
                },
                alignItems: 'center'
              }}
            >
              <Typography variant="body2" color="text.secondary">{item.section}</Typography>
              <Typography variant="body2" color="text.secondary">Day {item.day}</Typography>
              <Typography variant="body2" color="text.secondary">{item.item}</Typography>
              <Typography variant="body2" color="text.secondary">{item.note || '-'}</Typography>
              <Box>
                {item.url && (
                  <Tooltip title="Open Link">
                    <IconButton
                      size="small"
                      onClick={() => window.open(item.url, '_blank')}
                    >
                      <ExternalLink size={18} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box>
                <Tooltip title={item.dateDone ? `Completed on ${item.dateDone}` : 'Mark as Complete'}>
                  <IconButton
                    size="small"
                    onClick={() => !item.dateDone && handleMarkComplete(item.rowNumber)}
                    disabled={!!item.dateDone || completingItems[item.rowNumber!]}
                    color={item.dateDone ? 'success' : 'default'}
                  >
                    {completingItems[item.rowNumber!] ? (
                      <CircularProgress size={18} />
                    ) : item.dateDone ? (
                      <CheckCircle size={18} />
                    ) : (
                      <Circle size={18} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary">{item.dateDone || '-'}</Typography>
            </Box>
          ))}
        </InfiniteScroll>
      </Box>
    </Box>
  );
};
